// A platform persona's project override (ADR 0038 §2, a file of the app's design, Board #2639): what this
// project changes of the persona (its name here, the project's instructions after the persona's own or in their
// place, its guardrails, the tools it keeps), shown and edited on the persona's page. One save is one commit; the
// next chat turn runs the persona with it. The harness section has its own tab.
import { Loader2 } from 'lucide-react';
import * as React from 'react';
import { useAgentsHost, useAgentsQuery, useInvalidate, useAgentsKey } from './host.tsx';
import type { AgentTabContext } from './tabs.tsx';
import { btnGhost, btnPrimary, input, messageOf } from './ui.tsx';

interface Draft {
  title: string;
  instructions: string;
  mode: 'append' | 'replace';
  guardrails: string;
  tools: string[];
}

const label = 'block text-xs font-medium text-page-muted mb-1';

export function PersonaOverrideTab({ agent }: AgentTabContext) {
  const host = useAgentsHost();
  const k = useAgentsKey();
  const invalidate = useInvalidate();
  // The harness view says whether the caller may change the project's agents (the same rule as for this file).
  const view = useAgentsQuery(k('harness', agent.subject), () => host.client.harness(agent.subject));
  const o = agent.override ?? null;
  const available = o?.available_tools ?? agent.tools.map((t) => (t.group ? `${t.group}/${t.name}` : t.name));
  const [draft, setDraft] = React.useState<Draft | null>(null);
  const [busy, setBusy] = React.useState(false);
  const [error, setError] = React.useState('');
  const [note, setNote] = React.useState('');

  const start = () => {
    setError('');
    setNote('');
    setDraft({
      title: o?.title ?? '',
      instructions: o?.instructions ?? '',
      mode: o?.instructions_mode === 'replace' ? 'replace' : 'append',
      guardrails: o?.guardrails ? JSON.stringify(o.guardrails, null, 2) : '',
      tools: o?.tools ?? available,
    });
  };
  const save = async (d: Draft | null) => {
    setBusy(true);
    setError('');
    try {
      let guardrails: Record<string, unknown> | null = null;
      if (d?.guardrails.trim()) {
        try {
          guardrails = JSON.parse(d.guardrails) as Record<string, unknown>;
        } catch {
          throw new Error('The guardrails are not valid JSON.');
        }
      }
      const all = d ? d.tools.length === available.length : true;
      await host.client.savePersonaOverride(agent.key, {
        title: d?.title.trim() || null,
        instructions: d?.instructions.trim() ? d.instructions : null,
        instructions_mode: d?.instructions.trim() ? d.mode : null,
        guardrails,
        tools: all ? null : d!.tools,
      });
      setDraft(null);
      setNote(`Saved as one change. The next chat turn runs the persona with it.`);
      invalidate([k()]);
    } catch (e) {
      setError(messageOf(e));
    } finally {
      setBusy(false);
    }
  };

  if (view.loading && !view.data)
    return (
      <div className="flex items-center gap-2 text-sm text-page-muted">
        <Loader2 size={14} className="animate-spin" /> Reading the project’s settings…
      </div>
    );
  const editable = !!view.data?.editable;

  if (!draft)
    return (
      <div
        data-testid="persona-override"
        data-overridden={o ? 'true' : 'false'}
        className="space-y-3 text-sm"
      >
        {o ? (
          <>
            <div className="text-page-muted" data-testid="persona-override-file">
              This project overrides the persona in <span className="font-mono">{o.path}</span>
              {o.commit ? ` · commit ${o.commit.slice(0, 7)}` : ''}.
            </div>
            <dl className="grid grid-cols-[10rem_1fr] gap-x-3 gap-y-2 m-0">
              <dt className="text-page-muted">Name here</dt>
              <dd className="m-0">{o.title ?? '— (as it ships)'}</dd>
              <dt className="text-page-muted">Tools it keeps</dt>
              <dd className="m-0" data-testid="persona-override-tools">
                {o.tools ? o.tools.join(', ') : 'all of its own'}
              </dd>
              <dt className="text-page-muted">Guardrails</dt>
              <dd className="m-0">
                {o.guardrails ? (
                  <pre className="m-0 text-xs whitespace-pre-wrap">
                    {JSON.stringify(o.guardrails, null, 2)}
                  </pre>
                ) : (
                  '— (as it ships)'
                )}
              </dd>
              <dt className="text-page-muted">
                Instructions
                {o.instructions ? (o.instructions_mode === 'replace' ? ' (replace)' : ' (added)') : ''}
              </dt>
              <dd className="m-0">
                {o.instructions ? (
                  <pre
                    data-testid="persona-override-instructions"
                    className="m-0 whitespace-pre-wrap font-sans text-[13px] leading-relaxed"
                  >
                    {o.instructions}
                  </pre>
                ) : (
                  '— (as it ships)'
                )}
              </dd>
            </dl>
          </>
        ) : (
          <div className="text-page-muted">
            The persona runs as it ships. A project override changes its name here, adds the project’s
            instructions (or replaces its own), sets its guardrails and keeps a subset of its tools.
          </div>
        )}
        {editable ? (
          <div className="flex gap-2">
            <button type="button" className={btnGhost} data-testid="persona-override-edit" onClick={start}>
              {o ? 'Edit the override' : 'Override in this project'}
            </button>
            {o ? (
              <button
                type="button"
                className={btnGhost}
                disabled={busy}
                data-testid="persona-override-remove"
                onClick={() => void save(null)}
              >
                Remove the override
              </button>
            ) : null}
          </div>
        ) : null}
        {note ? <div className="text-xs text-emerald-700">{note}</div> : null}
        {error ? <div className="text-xs text-red-600">{error}</div> : null}
      </div>
    );

  const set = <K extends keyof Draft>(k: K, v: Draft[K]) => setDraft((d) => (d ? { ...d, [k]: v } : d));
  return (
    <div className="space-y-3 text-sm" data-testid="persona-override-form">
      <div>
        <label className={label} htmlFor="po-title">
          Name in this project (empty: “{agent.override?.title ? agent.title : agent.title}” as it ships)
        </label>
        <input
          id="po-title"
          className={input}
          value={draft.title}
          onChange={(e) => set('title', e.target.value)}
        />
      </div>
      <div>
        <label className={label} htmlFor="po-instructions">
          The project’s instructions
        </label>
        <select
          className={`${input} mb-1 w-auto`}
          value={draft.mode}
          onChange={(e) => set('mode', e.target.value as Draft['mode'])}
          data-testid="persona-override-mode"
        >
          <option value="append">Added after the persona’s own</option>
          <option value="replace">In place of the persona’s own</option>
        </select>
        <textarea
          id="po-instructions"
          rows={12}
          className={`${input} font-mono text-xs`}
          value={draft.instructions}
          onChange={(e) => set('instructions', e.target.value)}
        />
      </div>
      <div>
        <span className={label}>Tools it keeps</span>
        <div className="flex flex-wrap gap-3">
          {available.map((t) => (
            <label key={t} className="inline-flex items-center gap-1 text-xs">
              <input
                type="checkbox"
                checked={draft.tools.includes(t)}
                onChange={(e) =>
                  set('tools', e.target.checked ? [...draft.tools, t] : draft.tools.filter((x) => x !== t))
                }
              />
              <span className="font-mono">{t}</span>
            </label>
          ))}
        </div>
      </div>
      <div>
        <label className={label} htmlFor="po-guardrails">
          Guardrails (JSON: scope, grounding, verify, on_fail, refusal, max_tool_calls, disclose; empty: as it
          ships)
        </label>
        <textarea
          id="po-guardrails"
          rows={6}
          className={`${input} font-mono text-xs`}
          value={draft.guardrails}
          onChange={(e) => set('guardrails', e.target.value)}
        />
      </div>
      <div className="flex gap-2">
        <button
          type="button"
          className={btnPrimary}
          disabled={busy || !draft.tools.length}
          data-testid="persona-override-save"
          onClick={() => void save(draft)}
        >
          {busy ? <Loader2 size={14} className="animate-spin" /> : null} Save
        </button>
        <button type="button" className={btnGhost} disabled={busy} onClick={() => setDraft(null)}>
          Discard
        </button>
      </div>
      {error ? <div className="text-xs text-red-600">{error}</div> : null}
    </div>
  );
}
